import { NavBar } from "@/components/layout/NavBar";
import { SkipToMainLink } from "@/components/layout/SkipToMainLink";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../styles/demo-terminal.css";

const SKIP_TO_END_LINES = [
  `<span class="tp">$ </span><span class="tc">claude "Build a comprehensive landscape of GLP-1 receptor agonist clinical trials across ALL indications. For every drug, find every Phase 2/3 trial — diabetes, obesity, NASH, heart failure, CKD, Alzheimer's, sleep apnea, and more. Extract trial name, phase, N, primary endpoint, result, and FDA decision with line-level citations."</span>`,
  `<span class="tit">I'll search across FDA regulatory reviews, clinical trial registries, and published</span>`,
  `<span class="tit">papers to build a comprehensive landscape. Let me start with paperclip.</span>`,
  `<div class="tph">SEARCHING FDA (225K DOCUMENTS)</div>`,
  `<span class="tp">$ </span><span class="tc">paperclip search -s fda "GLP-1 receptor agonist NDA approval" -n 20</span>`,
  `<span class="to">  <span class="tpu">Found 20 documents</span></span>`,
  `<span class="to">  <span class="th">1.</span> Ozempic — NDA209637 · semaglutide · SUSTAIN trials</span>`,
  `<span class="to">  <span class="th">2.</span> Wegovy — NDA215256 · semaglutide 2.4mg · STEP trials</span>`,
  `<span class="to">  <span class="th">3.</span> Mounjaro — NDA215866 · tirzepatide · SURPASS trials</span>`,
  `<span class="to">  <span class="th">4.</span> Zepbound — NDA217806 · tirzepatide · SURMOUNT trials</span>`,
  `<span class="to">  <span class="td">  ... 16 more FDA review docs</span></span>`,
  `<div class="tph">SEARCHING CLINICAL TRIALS (580K+ PROTOCOLS)</div>`,
  `<span class="tp">$ </span><span class="tc">paperclip search -s trials "semaglutide tirzepatide phase 3" -n 20</span>`,
  `<span class="to">  <span class="tpu">Found 20 trials</span></span>`,
  `<span class="to">  <span class="th">1.</span> NCT03548935 · SELECT · CV outcomes · N=17,604</span>`,
  `<span class="to">  <span class="th">2.</span> NCT04625985 · FLOW · CKD · N=3,533</span>`,
  `<span class="to">  <span class="th">3.</span> NCT04895982 · STEP HFpEF · Heart failure · N=529</span>`,
  `<span class="to">  <span class="td">  ... searching tirzepatide, dulaglutide, liraglutide...</span></span>`,
  `<div class="tph">READING PUBLISHED RESULTS (7.5M PAPERS)</div>`,
  `<span class="tp">$ </span><span class="tc">paperclip search -s pmc "SUSTAIN STEP SURPASS SELECT FLOW phase 3 results" -n 15</span>`,
  `<span class="to">  <span class="tpu">Found 15 papers</span></span>`,
  `<span class="to">  <span class="th">1.</span> STEP 1 — semaglutide 2.4mg: -14.9% body weight (PMC7928659)</span>`,
  `<span class="to">  <span class="th">2.</span> SELECT — 20% CV risk reduction, HR 0.80 (PMC11271387)</span>`,
  `<span class="to">  <span class="th">3.</span> FLOW — 24% kidney risk reduction, HR 0.76 (PMC11282385)</span>`,
  `<div class="tph">EXTRACTING ACROSS RESULTS WITH MAP</div>`,
  `<span class="tp">$ </span><span class="tc">paperclip map --from s_87b3b489 "Extract trial name, N, primary endpoint, comparator, result, approval status"</span>`,
  `<span class="to">  <span class="td">⟳ Reading 20 FDA documents...</span></span>`,
  `<span class="to">  <span class="tgr">✓</span> Extracted 68 trial entries across 13 drugs</span>`,
  `<div class="tph">VERIFYING WITH LINE-LEVEL CITATIONS</div>`,
  `<span class="tp">$ </span><span class="tc">paperclip grep "primary endpoint" /fda/fda_bed63670b617/content.lines</span>`,
  `<span class="to">  <span class="tcy">L351:</span> SUSTAIN 1 — 388 patients, HbA1c at 30 wk</span>`,
  `<span class="to">  <span class="tcy">L361:</span> SUSTAIN 2 — 1,231 patients vs sitagliptin</span>`,
  `<span class="to">  <span class="td">  →</span> <span style="color:#8B6CC1">citations.gxl.ai/fda/fda_bed63670b617#L351</span></span>`,
  `<div class="tph">CROSS-DATABASE TABLE</div>`,
  `<span class="to">  <span class="td">Merging FDA + trials + PMC across 18 drugs × 12 indications...</span></span>`,
  ``,
  `<span class="to">  <span class="th">Drug          Indication    Trial       N        Result              Source</span></span>`,
  `<span class="to">  <span class="td">───────────────────────────────────────────────────────────────────────────────</span></span>`,
  `<span class="to">  Semaglutide  T2D           SUSTAIN 1   388      −1.5% HbA1c         <span style="color:#8B6CC1">FDA L351</span></span>`,
  `<span class="to">  Semaglutide  Obesity       STEP 1      1,961    −14.9% body wt      <span style="color:#4A9FB0">PMC L31</span></span>`,
  `<span class="to">  Semaglutide  CV outcomes   SELECT      17,604   HR 0.80 (0.72–0.90) <span style="color:#4A9FB0">PMC L45</span></span>`,
  `<span class="to">  Semaglutide  CKD           FLOW        3,533    HR 0.76 (0.66–0.88) <span style="color:#4A9FB0">PMC L33</span></span>`,
  `<span class="to">  Semaglutide  Heart failure STEP-HFpEF  529      +7.5 vs +1.5 pts    <span style="color:#4A7FBF">TRIAL</span></span>`,
  `<span class="to">  Tirzepatide  T2D           SURPASS-2   1,879    −2.0% HbA1c         <span style="color:#8B6CC1">FDA L76</span></span>`,
  `<span class="to">  Tirzepatide  Obesity       SURMOUNT-1  2,539    −20.9% body wt      <span style="color:#8B6CC1">FDA L142</span></span>`,
  `<span class="to">  Tirzepatide  Sleep apnea   SURMOUNT    469      −30 events/hr       <span style="color:#4A7FBF">TRIAL</span></span>`,
  `<span class="to">  Liraglutide  CV outcomes   LEADER      9,340    HR 0.87 (0.78–0.97) <span style="color:#4A7FBF">TRIAL L177</span></span>`,
  `<span class="to">  Liraglutide  Obesity       SCALE       3,731    −7.4% body wt       <span style="color:#8B6CC1">FDA L236</span></span>`,
  `<span class="to">  Dulaglutide  CV outcomes   REWIND      9,901    HR 0.88 (0.79–0.99) <span style="color:#8B6CC1">FDA L364</span></span>`,
  `<span class="to">  Exenatide   CV outcomes   EXSCEL      14,752   HR 0.91 (neutral)   <span style="color:#4A7FBF">TRIAL L213</span></span>`,
  `<span class="to">  <span class="td">  ... 123 more rows across 18 drugs × 12 indications</span></span>`,
  ``,
  `<span class="tgr">✓ glp1-landscape.html — 18 drugs · 12 indications · 135 trials · 100 citations</span>`,
  `<span class="to">  <span class="td">Sources: FDA (225K docs) + ClinicalTrials.gov (580K+) + PMC (7.5M papers)</span></span>`,
];

export const DemoPage = () => {
  useDocumentTitle("Demo");
  const termRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const term = termRef.current;
    const cta = ctaRef.current;
    if (!term || !cta) return;

    let runId = 0;
    let skipped = false;

    const tAdd = (html: string) => {
      const d = document.createElement("div");
      d.className = "tl";
      d.innerHTML = html;
      term.appendChild(d);
      term.scrollTop = term.scrollHeight;
    };

    const tCmd = (text: string, cb: () => void) => {
      const myId = runId;
      const d = document.createElement("div");
      d.className = "tl";
      term.appendChild(d);
      let i = 0;
      d.innerHTML = `<span class="tp">$ </span><span class="cur"></span>`;
      const t = () => {
        if (myId !== runId) return;
        if (i < text.length) {
          d.innerHTML = `<span class="tp">$ </span><span class="tc">${text.slice(0, i + 1)}</span><span class="cur"></span>`;
          i++;
          term.scrollTop = term.scrollHeight;
          setTimeout(t, 14 + Math.random() * 8);
        } else {
          d.innerHTML = `<span class="tp">$ </span><span class="tc">${text}</span>`;
          term.scrollTop = term.scrollHeight;
          if (myId === runId) cb();
        }
      };
      setTimeout(t, 80);
    };

    type Step = { d: number; fn: () => void };

    const runSteps = (steps: Step[], i: number, myRunId: number) => {
      if (i >= steps.length || myRunId !== runId) return;
      setTimeout(() => {
        if (myRunId !== runId) return;
        steps[i].fn();
        runSteps(steps, i + 1, myRunId);
      }, steps[i].d);
    };

    const STEPS: Step[] = [
      { d: 400, fn: () => tCmd(`claude "Build a comprehensive landscape of GLP-1 receptor agonist clinical trials across ALL indications. For every drug, find every Phase 2/3 trial — diabetes, obesity, NASH, heart failure, CKD, Alzheimer's, sleep apnea, and more. Extract trial name, phase, N, primary endpoint, result, and FDA decision with line-level citations."`, () => {
        setTimeout(() => {
          tAdd(`<span class="tit">I'll search across FDA regulatory reviews, clinical trial registries, and published</span>`);
          tAdd(`<span class="tit">papers to build a comprehensive landscape. Let me start with paperclip.</span>`);
          setTimeout(() => {
            tAdd(`<div class="tph">SEARCHING FDA (225K DOCUMENTS)</div>`);
            tCmd(`paperclip search -s fda "GLP-1 receptor agonist NDA approval" -n 20`, () => {});
            runSteps(STEPS, 3, runId);
          }, 1200);
        }, 800);
      })},
      { d: 999999, fn: () => {} },
      { d: 999999, fn: () => {} },
      { d: 1800, fn: () => { tAdd(`<span class="to">  <span class="tpu">Found 20 documents</span></span>`); tAdd(`<span class="to">  <span class="th">1.</span> Ozempic — NDA209637 · semaglutide · SUSTAIN trials</span>`); tAdd(`<span class="to">  <span class="th">2.</span> Wegovy — NDA215256 · semaglutide 2.4mg · STEP trials</span>`); tAdd(`<span class="to">  <span class="th">3.</span> Mounjaro — NDA215866 · tirzepatide · SURPASS trials</span>`); tAdd(`<span class="to">  <span class="th">4.</span> Zepbound — NDA217806 · tirzepatide · SURMOUNT trials</span>`); tAdd(`<span class="to">  <span class="td">  ... 16 more FDA review docs</span></span>`); }},
      { d: 1200, fn: () => { tAdd(`<div class="tph">SEARCHING CLINICAL TRIALS (580K+ PROTOCOLS)</div>`); tCmd(`paperclip search -s trials "semaglutide tirzepatide phase 3" -n 20`, () => {}); }},
      { d: 1600, fn: () => { tAdd(`<span class="to">  <span class="tpu">Found 20 trials</span></span>`); tAdd(`<span class="to">  <span class="th">1.</span> NCT03548935 · SELECT · CV outcomes · N=17,604</span>`); tAdd(`<span class="to">  <span class="th">2.</span> NCT04625985 · FLOW · CKD · N=3,533</span>`); tAdd(`<span class="to">  <span class="th">3.</span> NCT04895982 · STEP HFpEF · Heart failure · N=529</span>`); tAdd(`<span class="to">  <span class="td">  ... searching tirzepatide, dulaglutide, liraglutide...</span></span>`); }},
      { d: 1000, fn: () => { tAdd(`<div class="tph">READING PUBLISHED RESULTS (7.5M PAPERS)</div>`); tCmd(`paperclip search -s pmc "SUSTAIN STEP SURPASS SELECT FLOW phase 3 results" -n 15`, () => {}); }},
      { d: 1600, fn: () => { tAdd(`<span class="to">  <span class="tpu">Found 15 papers</span></span>`); tAdd(`<span class="to">  <span class="th">1.</span> STEP 1 — semaglutide 2.4mg: -14.9% body weight (PMC7928659)</span>`); tAdd(`<span class="to">  <span class="th">2.</span> SELECT — 20% CV risk reduction, HR 0.80 (PMC11271387)</span>`); tAdd(`<span class="to">  <span class="th">3.</span> FLOW — 24% kidney risk reduction, HR 0.76 (PMC11282385)</span>`); }},
      { d: 1000, fn: () => { tAdd(`<div class="tph">EXTRACTING ACROSS RESULTS WITH MAP</div>`); tCmd(`paperclip map --from s_87b3b489 "Extract trial name, N, primary endpoint, comparator, result, approval status"`, () => {}); }},
      { d: 1600, fn: () => { tAdd(`<span class="to">  <span class="td">⟳ Reading 20 FDA documents...</span></span>`); tAdd(`<span class="to">  <span class="tgr">✓</span> Extracted 68 trial entries across 13 drugs</span>`); }},
      { d: 1000, fn: () => { tAdd(`<div class="tph">VERIFYING WITH LINE-LEVEL CITATIONS</div>`); tCmd(`paperclip grep "primary endpoint" /fda/fda_bed63670b617/content.lines`, () => {}); }},
      { d: 1400, fn: () => { tAdd(`<span class="to">  <span class="tcy">L351:</span> SUSTAIN 1 — 388 patients, HbA1c at 30 wk</span>`); tAdd(`<span class="to">  <span class="tcy">L361:</span> SUSTAIN 2 — 1,231 patients vs sitagliptin</span>`); tAdd(`<span class="to">  <span class="td">  →</span> <span style="color:#8B6CC1">citations.gxl.ai/fda/fda_bed63670b617#L351</span></span>`); }},
      { d: 1000, fn: () => { tAdd(`<div class="tph">CROSS-DATABASE TABLE</div>`); tAdd(`<span class="to">  <span class="td">Merging FDA + trials + PMC across 18 drugs × 12 indications...</span></span>`); }},
      { d: 500, fn: () => { tAdd(`<span class="to"></span>`); tAdd(`<span class="to">  <span class="th">Drug          Indication    Trial       N        Result              Source</span></span>`); tAdd(`<span class="to">  <span class="td">───────────────────────────────────────────────────────────────────────────────</span></span>`); }},
      { d: 150, fn: () => tAdd(`<span class="to">  Semaglutide  T2D           SUSTAIN 1   388      −1.5% HbA1c         <span style="color:#8B6CC1">FDA L351</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  Semaglutide  Obesity       STEP 1      1,961    −14.9% body wt      <span style="color:#4A9FB0">PMC L31</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  Semaglutide  CV outcomes   SELECT      17,604   HR 0.80 (0.72–0.90) <span style="color:#4A9FB0">PMC L45</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  Semaglutide  CKD           FLOW        3,533    HR 0.76 (0.66–0.88) <span style="color:#4A9FB0">PMC L33</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  Semaglutide  Heart failure STEP-HFpEF  529      +7.5 vs +1.5 pts    <span style="color:#4A7FBF">TRIAL</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  Tirzepatide  T2D           SURPASS-2   1,879    −2.0% HbA1c         <span style="color:#8B6CC1">FDA L76</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  Tirzepatide  Obesity       SURMOUNT-1  2,539    −20.9% body wt      <span style="color:#8B6CC1">FDA L142</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  Tirzepatide  Sleep apnea   SURMOUNT    469      −30 events/hr       <span style="color:#4A7FBF">TRIAL</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  Liraglutide  CV outcomes   LEADER      9,340    HR 0.87 (0.78–0.97) <span style="color:#4A7FBF">TRIAL L177</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  Liraglutide  Obesity       SCALE       3,731    −7.4% body wt       <span style="color:#8B6CC1">FDA L236</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  Dulaglutide  CV outcomes   REWIND      9,901    HR 0.88 (0.79–0.99) <span style="color:#8B6CC1">FDA L364</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  Exenatide   CV outcomes   EXSCEL      14,752   HR 0.91 (neutral)   <span style="color:#4A7FBF">TRIAL L213</span></span>`) },
      { d: 150, fn: () => tAdd(`<span class="to">  <span class="td">  ... 123 more rows across 18 drugs × 12 indications</span></span>`) },
      { d: 600, fn: () => { tAdd(`<span class="to"></span>`); tAdd(`<span class="tgr">✓ glp1-landscape.html — 18 drugs · 12 indications · 135 trials · 100 citations</span>`); tAdd(`<span class="to">  <span class="td">Sources: FDA (225K docs) + ClinicalTrials.gov (580K+) + PMC (7.5M papers)</span></span>`); cta.style.opacity = "1"; }},
    ];

    const skipToEnd = () => {
      if (skipped) return;
      skipped = true;
      runId++;

      term.innerHTML = SKIP_TO_END_LINES
        .map(h => `<div class="tl" style="opacity:1">${h}</div>`)
        .join("\n");

      term.scrollTop = 0;
      cta.style.opacity = "1";
    };

    const handleClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest(".cta")) return;
      skipToEnd();
    };

    document.addEventListener("click", handleClick);

    const startTimer = setTimeout(() => runSteps(STEPS, 0, runId), 500);

    return () => {
      runId++;
      document.removeEventListener("click", handleClick);
      clearTimeout(startTimer);
    };
  }, []);

  return (
    <div className="h-screen h-[100dvh] overflow-hidden bg-[#F7F5F0] flex flex-col">
      <SkipToMainLink />
      <NavBar />
      <main id="main-content" className="demo-terminal flex-1 min-h-0">
        <div className="terminal">
          <div className="terminal-bar">
            <div className="terminal-dot dot-r" />
            <div className="terminal-dot dot-y" />
            <div className="terminal-dot dot-g" />
            <div className="terminal-title">claude code + paperclip</div>
          </div>
          <div className="terminal-body" ref={termRef} />
        </div>
        <div className="cta" ref={ctaRef}>
          <Link to="/demo/glp1-landscape">View full report →</Link>
        </div>
      </main>
    </div>
  );
};
